import React, { useEffect, useState } from "react";

const SavingsModal = ({ isOpen, onClose, amount, onNavigate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to trigger animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  if (!isOpen && !isVisible) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Add ripple effect
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add("ripple");

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    onNavigate();
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-all duration-500 ease-out ${
          isVisible ? "opacity-100" : "opacity-0"
        } ${isOpen ? "bg-opacity-30" : "bg-opacity-0"}`}
        onClick={handleBackdropClick}
      >
        <div
          className={`bg-white rounded-3xl max-w-md w-full mx-4 px-6 sm:px-8 py-5 sm:py-6 text-center shadow-2xl relative transform transition-all duration-500 ease-out ${
            isAnimating && isOpen
              ? "scale-100 opacity-100 translate-y-0"
              : "scale-90 opacity-0 translate-y-8"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated background gradient */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-[#8AB9F1] via-[#B57EDC] to-[#F08080] rounded-full opacity-10 blur-3xl animate-pulse"></div>
            <div
              className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-[#F08080] via-[#B57EDC] to-[#8AB9F1] rounded-full opacity-10 blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          {/* Dynamic amount section */}
          <div className="relative flex flex-col items-center z-10">
            <h2
              className={`text-2xl font-bold text-[#141A33] mb-2 transition-all duration-700 delay-100 ease-out ${
                isAnimating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              Save
            </h2>
            <p
              className={`text-5xl sm:text-6xl font-extrabold bg-gradient-to-r from-[#8AB9F1] via-[#B57EDC] to-[#F08080] text-transparent bg-clip-text mb-2 transition-all duration-700 delay-200 ease-out ${
                isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
            >
              £{amount?.toLocaleString() || "0"}
            </p>
            <span
              className={`text-gray-500 text-sm transition-all duration-700 delay-300 ease-out ${
                isAnimating ? "opacity-100" : "opacity-0"
              }`}
            >
              a year
            </span>
          </div>

          {/* Message */}
          <div className="relative z-10 mt-5">
            <h3
              className={`text-2xl font-bold text-[#141A33] mb-3 transition-all duration-700 delay-300 ease-out ${
                isAnimating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              Great news
            </h3>
            <p
              className={`text-gray-600 text-base transition-all duration-700 delay-400 ease-out ${
                isAnimating ? "opacity-100" : "opacity-0"
              }`}
            >
              You can save{" "}
              <span className="font-bold text-[#28a745]">
                £{amount?.toLocaleString() || "0"}
              </span>{" "}
              a year with Savy
            </p>
          </div>

          {/* Button */}
          <button
            onClick={handleButtonClick}
            className={`mt-6 w-full bg-[#002147] text-white py-3.5 rounded-xl font-semibold hover:bg-[#001730] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden z-10 ${
              isAnimating
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: isAnimating ? "500ms" : "0ms" }}
          >
            <span className="relative z-10">Next →</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        .ripple {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.6);
          transform: scale(0);
          animation: ripple 600ms ease-out;
          pointer-events: none;
          z-index: 1;
        }
      `}</style>
    </>
  );
};

export default SavingsModal;
